import { AxiosResponse } from "axios"
import { useApiCommand } from "hooks/core/useApiCommand"
import { pollAsync, fetchAsyncResult } from "api/Engine/data-reporting"

// Define the return type of the hook
interface UseAsyncReturn {
  pollingAsync: (asyncID: string) => Promise<AxiosResponse<unknown>>
  isPollingAsync: boolean
  getAsyncResult: (asyncID: string) => Promise<AxiosResponse<unknown>>
  isFetchingAsyncResult: boolean
}

/**
 * Custom hook for managing asynchronous polling and result fetching API calls for data reporting.
 *
 * @returns {UseAsyncReturn} - An object containing functions for polling and fetching asynchronous results, along with their loading states.
 *
 * @example
 * const {
 *   pollingAsync,
 *   isPollingAsync,
 *   getAsyncResult,
 *   isFetchingAsyncResult,
 * } = useAsync();
 */
export const useAsync = (): UseAsyncReturn => {
  const [pollingAsync, isPollingAsync] = useApiCommand(pollAsync)
  const [getAsyncResult, isFetchingAsyncResult] = useApiCommand(fetchAsyncResult)

  return {
    /**
     * Polls the server to check if an asynchronous request is ready.
     * @param asyncID - The identifier for the asynchronous request to check readiness for.
     * @returns Promise resolving to the API response
     */
    pollingAsync,

    /**
     * Indicates whether an async polling request is currently in progress.
     */
    isPollingAsync,

    /**
     * Fetches the result of a completed asynchronous request.
     * @param asyncID - The identifier for the asynchronous request whose result is being fetched.
     * @returns Promise resolving to the API response containing the result data
     */
    getAsyncResult,

    /**
     * Indicates whether an async result fetch is currently in progress.
     */
    isFetchingAsyncResult,
  }
}
